import { useEffect, useState } from "react";
import { IFormSection, TFormHookParams } from "../../types/common";
import useGet from "../common/useGet";
import {
  API_ACCOUNT_SERVICES,
  API_ACCOUNTS_ROLES,
  API_DEPARTMENTS,
  API_DISTRICTS,
  API_ORGANISATIONS,
  API_PARKINGS_BY_DISTRICT,
} from "../../utils/apis";
import {
  addSelectItem,
  getRegister,
  // getRegister,
  getResultOptions,
  useGetUserPermissions,
} from "../../utils/helpers";
// import { URL_USERS } from "../../utils/urls";
import { useGetOwnerDetails } from "../../utils/owner_details";
import { CAN_ADD_ORGANISATION, SELECT } from "../../utils/constants";
import { useAuth } from "../../context/AuthContext";
import {
  IsAdmin,
  isAgentSelected,
  IsSuperAdmin,
} from "../../utils/permissions";
import { URL_USERS } from "../../utils/urls";

export const useUsersForm = ({
  id,
  form,
  updateFormValues,
  setError,
}: TFormHookParams = {}) => {
  const isUpdate = id ? true : false;
  const { loadData: getRoles, data: roles } = useGet();
  const { loadData: getOrganisation, data: organisation } = useGet();
  const { loadData: getDistricts, data: districts } = useGet();

  const [groups, setGroups] = useState<any>(form?.groups);

  const isAdmin = IsAdmin();
  const isSuperAdmin = IsSuperAdmin();

  // Use the original hook name
  const ownerDetails = useGetOwnerDetails({
    form,
    updateFormValues,
    setError,
  });

  const { user } = useAuth();

  // Disable certain fields for updates and preserve values
  const ownerDetailsDisabled = ownerDetails.map((field) => {
    if (field.field_name === "first_name" || field.field_name === "last_name") {
      // Get the value from the form object based on field name
      let fieldValue = "";

      if (field.field_name === "first_name") {
        fieldValue = form?.first_name || "";
      } else if (field.field_name === "last_name") {
        fieldValue = form?.last_name || "";
      }

      const updatedField = {
        ...field,
        props: {
          ...field.props,
          disabled: isUpdate,
          value: fieldValue,
        },
      };
      return updatedField;
    }
    return field;
  });

  const permissions = useGetUserPermissions();

  const {
    loadData: loadParkingsUnderStreet,
    loading: loadingParkingsUnderStreet,
    data: parkingsUnderStreetData,
  } = useGet();

  const {
    loadData: loadServices,
    loading: loadingServices,
    data: servicesData,
  } = useGet();

  // Get the selected street from form
  const { street } = form || {};

  useEffect(() => {
    getDistricts({ api: API_DISTRICTS });
  }, []);

  useEffect(() => {
    const districtId = form?.district || form?.profile?.district;
    const isAgent = isAgentSelected(form?.groups, roles?.results);

    if (districtId && isAgent) {
      loadParkingsUnderStreet({
        api: `${API_PARKINGS_BY_DISTRICT}?district_id=${districtId}`,
      });
    }
  }, [form?.district, form?.profile?.district, form?.groups, roles?.results]);

  useEffect(() => {
    loadServices({ api: API_ACCOUNT_SERVICES });
  }, []);

  useEffect(() => {
    if (updateFormValues && street) {
      updateFormValues((currentValues) => ({
        ...currentValues,
        parking: currentValues.parking ? null : currentValues.parking,
        service: currentValues.service ? null : currentValues.service,
      }));
    }
  }, [street, updateFormValues]);

  useEffect(() => {
    getRoles({ api: API_ACCOUNTS_ROLES });
    getOrganisation({ api: API_ORGANISATIONS });
    // setGroups(form?.groups);
  }, []);

  useEffect(() => {
    if (form?.groups !== groups) {
      setGroups(form?.groups);
    }
  }, [form?.groups]);

  // Data transformation function to handle profile fields
  const transformFormData = (formData) => {
    if (!formData) return formData;

    const transformed = { ...formData };

    // Handle profile_nationality field - move it inside profile object
    if (formData.profile_nationality) {
      transformed.profile = {
        ...(transformed.profile || {}),
        nationality: formData.profile_nationality,
      };
      delete transformed.profile_nationality;
    }

    // Handle tin field - move it inside profile object
    if (formData.tin) {
      transformed.profile = {
        ...(transformed.profile || {}),
        tin: formData.tin,
      };
      delete transformed.tin;
    }

    // Handle physical_address field - move it inside profile object
    if (formData.physical_address) {
      transformed.profile = {
        ...(transformed.profile || {}),
        physical_address: formData.physical_address,
      };
      delete transformed.physical_address;
    }

    // Handle street field - move it inside profile object
    if (formData.street) {
      transformed.profile = {
        ...(transformed.profile || {}),
        street: formData.street,
      };
      delete transformed.street;
    }

    // Handle parking field - move it inside profile object
    if (formData.parking) {
      transformed.profile = {
        ...(transformed.profile || {}),
        parking: formData.parking,
      };
      delete transformed.parking;
    }

    if (formData.service) {
      transformed.profile = {
        ...(transformed.profile || {}),
        service: formData.service,
      };
      delete transformed.service;
    }

    return transformed;
  };

  // Prepare parking options data
  // Merge user's existing parkings with available parkings from district
  const parkingOptions = (() => {
    const options: any[] = [];

    // Add parkings from district API
    if (parkingsUnderStreetData?.length) {
      parkingsUnderStreetData.forEach((parking) => {
        options.push(
          addSelectItem({
            label: parking.name,
            value: parking.id,
          }),
        );
      });
    }

    if (isUpdate && form?.profile?.parking && form?.profile?.parking_names) {
      const existingIds = new Set(options.map((opt) => opt.value));

      form.profile.parking.forEach((parkingId: string, index: number) => {
        if (!existingIds.has(parkingId) && form.profile.parking_names[index]) {
          options.push(
            addSelectItem({
              label: form.profile.parking_names[index],
              value: parkingId,
            }),
          );
        }
      });
    }

    return options;
  })();

  const sections: IFormSection[] = [
    {
      title: "User Details",
      fields: [
        ...ownerDetailsDisabled,
        {
          field_name: "system_instance",
          type: "select",
          label: "Organisation",
          props: { required: true },
          options_data: organisation?.results,
          visible:
            permissions.includes(CAN_ADD_ORGANISATION) && user?.is_superuser,
        },
      ],
    },

    {
      title: "Location Details",

      fields: [
        {
          type: SELECT,
          field_name: "district",
          options_data: districts,
          props: {
            required: !isUpdate,
            disabled: isUpdate && !isSuperAdmin && !isAdmin,
          },
          value: form?.profile?.district,
        },
      ],
    },

    // ROLES - Only show if account_type is STAFF
    {
      title: "Account Details",
      visible:
        // itemExists(permissions, [
        //  CAN_ADMIN
        // ]),
        true,

      fields: [
        {
          field_name: "groups",
          type: "select",
          label: "System Roles",
          props: {
            required: true,
            disabled: isUpdate && !isSuperAdmin && !isAdmin,
          },

          // props: {
          //   required: true,
          //   onChange: (value) => {
          //     setGroups(value);
          //     // Make sure to update the form values immediately
          //     updateFormValues({ groups: value });
          //   }
          // },

          options_data: roles?.results,
          value: groups,
        },

        // {
        //   field_name: "street",
        //   type: "select",
        //   label: "Street",
        //   props: { required: true },
        //   options_data: streetsData?.results || streetsData,
        //   loading: loadingStreets,
        //   visible: isAgentSelected(groups, roles?.results),
        // },

        {
          field_name: "parking",
          type: "select",
          label: "Parking",
          value: form?.profile?.parking,
          props: {
            required: true,
            multiple: true,
            placeholder: street
              ? loadingParkingsUnderStreet
                ? "Loading parkings..."
                : "Select a parking"
              : "Select a parking",
            // disabled: !street || loadingParkingsUnderStreet,
          },
          options_data: parkingOptions,
          loading: loadingParkingsUnderStreet,

          // visible: isAgentSelected(groups, roles?.results) && !!street,
          visible: isAgentSelected(groups, roles?.results),
        },

        {
          field_name: "service",
          type: "select",
          label: "Services",
          value: form?.profile?.service,
          props: {
            required: false,
            multiple: true,
            placeholder: loadingServices
              ? "Loading services..."
              : "Select services",
          },
          options_data: servicesData,
          loading: loadingServices,
        },
      ],
    },
  ];

  const title = "Register User";
  // const redirect = getRegister(URL_USERS);
  const redirect = isSuperAdmin || isAdmin ? getRegister(URL_USERS) : null;

  const getConfigOptions = () => {
    const { loadData: getDepartments, data: departments } = useGet();

    useEffect(() => {
      getDepartments({
        api: API_DEPARTMENTS,
        params: {},
      });
    }, []);

    return {
      departments: getResultOptions(departments, "name", "id"),
    };
  };

  const options = {
    ...getConfigOptions(),
  };

  return {
    sections,
    title,
    redirect,
    options,
    transformFormData,
  };
};
